import { FormikProvider, useFormik } from 'formik';
import * as yup from 'yup';
import CurrencyInput from '@/modules/common/components/CurrencyInput.tsx';
import { SelectPortfolioBrokers } from '@/modules/issuer-dashboard/components/portfolios/SelectPortfolioBrokers.tsx';
import {
  useIssuerAddPortfolioAssetMutation,
  useIssuerUpdatePortfolioAssetMutation,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import {
  BaseModalProps,
  Button,
  ModalContentWrapper,
  Select,
  SelectPaginateOption,
  TextInputField,
  getSelectOptionByEnum,
} from 'ui';
import { fixNameEnum } from 'ui/utils';
import { AssetExtended, AssetType, CreateAssetRequest, CurrencyEnum, GicsIndustry } from 'api';

const validationSchema = yup.object<CreateAssetRequest>({
  name: yup.string().trim().required('Asset name is required').min(1, 'Asset name is too short'),
  portfolio_id: yup
    .number()
    .moreThan(0, 'This field is required')
    .required('This field is required'),
  asset_type: yup
    .mixed<AssetType>()
    .oneOf(Object.values(AssetType))
    .required('This field is required'),
  price_per_share: yup
    .number()
    .typeError('Price must be a number')
    .moreThan(0, 'Price must be more than 0')
    .required('This field is required'),
  currency: yup
    .mixed<CurrencyEnum>()
    .oneOf(Object.values(CurrencyEnum))
    .required('This field is required'),
  broker_id: yup.number().nullable(),
});

export interface IssuerAddUpdateAssetModalProps extends BaseModalProps {
  item?: AssetExtended;
  portfolioId: number;
  onSuccess?: () => void;
}

export const IssuerAddUpdateAssetModal = ({
  open,
  onClose,
  item,
  portfolioId,
  onSuccess,
}: IssuerAddUpdateAssetModalProps) => {
  const formik = useFormik<CreateAssetRequest>({
    validationSchema,
    initialValues: {
      name: item?.name || '',
      portfolio_id: portfolioId,
      broker_id: item?.broker_id || undefined,
      asset_type: item?.asset_type || undefined,
      risks: undefined,
      price_per_share: item?.price_per_share || '',
      currency: item?.currency || undefined,
      industry: item?.industry || undefined,
    } as CreateAssetRequest,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async values => {
      if (item) {
        await mutateAsyncUpdate({
          ...values,
          id: item.id,
        });
      } else {
        await mutateAsyncAdd(values);
      }
      formik.resetForm();
      onClose();
    },
  });

  const { mutateAsync: mutateAsyncAdd, isLoading: isLoadingAdd } =
    useIssuerAddPortfolioAssetMutation({
      setErrors: formik.setErrors,
      onSuccess,
    });
  const { mutateAsync: mutateAsyncUpdate, isLoading: isLoadingUpdate } =
    useIssuerUpdatePortfolioAssetMutation({
      setErrors: formik.setErrors,
      onSuccess,
    });

  const handleChangeSelect = (field: string) => (option: string | boolean | null | any) => {
    formik.setFieldValue(field, option);
    formik.setFieldError(field, '');
  };

  const handleChangeSelectObjectId = (
    options: SelectPaginateOption<{ id: number }> | null,
    field: string,
  ) => {
    formik.setFieldValue(field, options?.value?.id ?? null);
    formik.setFieldError(field, '');
  };

  const handleChangePricePerShare = (pricePerShare: number | string) => {
    formik.setFieldValue('price_per_share', pricePerShare);
    formik.setFieldError('price_per_share', '');
  };

  return (
    <ModalContentWrapper
      onClose={onClose}
      open={open}
      title={item ? 'Update asset' : 'New asset'}
      classes={{ children: 'p-0' }}
    >
      <FormikProvider value={formik}>
        <div className="p-3 sm:p-6 flex flex-col w-full gap-6">
          <TextInputField name="name" label="Name" placeholder="Enter asset name..." />

          <Select
            label="Asset Type"
            options={getSelectOptionByEnum<AssetType>(AssetType, false, false)}
            value={formik.values.asset_type}
            onChange={handleChangeSelect('asset_type')}
            error={formik.errors.asset_type}
            isClearable={true}
          />

          <CurrencyInput
            name="price_per_share"
            label={`price per share/unit`}
            value={formik.values.price_per_share}
            error={formik.errors.price_per_share}
            onChange={handleChangePricePerShare}
            decimalPoints={6}
          />

          <Select
            isClearable={true}
            value={formik.values.currency}
            placeholder="Select currency"
            label="Currency"
            positionMenu="down"
            onChange={handleChangeSelect('currency')}
            options={Object.entries(CurrencyEnum).map(item => ({
              label: (
                <span className="flex gap-4 items-center font-[Inter] text-sm leading-5">
                  <span className="text-gray-900">{item[1].toUpperCase()}</span>
                </span>
              ),
              value: item[1],
              toString: () => fixNameEnum(item[0]),
            }))}
            error={formik.errors.currency}
          />

          <SelectPortfolioBrokers
            portfolioId={portfolioId}
            label="Select Broker (optional)"
            onChange={handleChangeSelectObjectId}
            value={formik.values.broker_id}
            error={formik.errors.broker_id}
            clearable={true}
            formFieldTitle="broker_id"
            placeholder="Select Broker"
          />

          <Select
            label="Industry (optional)"
            options={getSelectOptionByEnum<GicsIndustry>(GicsIndustry, false, false)}
            value={formik.values.industry}
            onChange={handleChangeSelect('industry')}
            isClearable={true}
            placeholder="Select industry..."
          />
          {/* <TextInputField name="risks" label="Risks" /> */}
        </div>
        <div className="mt-4 p-3 !pb-0 sm:p-6">
          <Button
            type="submit"
            onClick={formik.handleSubmit}
            loading={isLoadingAdd || isLoadingUpdate}
          >
            {item ? 'Update asset' : 'Add new asset'}
          </Button>
        </div>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
